import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Upload, Download, CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import api from '@/admin/lib/api'
import { FileDropzone } from '@/shared/components/ui/file-dropzone'
import { toastApiError } from '@/shared/lib/api-error'
import {
  MetadataField,
  normaliseClaimField,
  FILE_FIELD_TYPES,
} from '@/shared/lib/forms'
import {
  useBulkImportTargets,
  useBulkImportSchema,
  useBulkImportValidate,
  useBulkImportCommit,
  useBulkImportJob,
  useSaveBulkImportColumnMap,
  type BulkImportTarget,
  type BulkImportPreviewRow,
  type BulkImportJobResultRow,
} from '@/admin/hooks/use-bulk-import'

/**
 * Generic, tenant-agnostic bulk-import dialog. The only thing that differs per
 * injection site is the `target` token (registration | claim:<schema> |
 * hoa_claim). Uploads a CSV/Excel, previews it (server-parsed dry-run), then
 * commits an async job and polls for results. See docs/bulk-import-engine.md.
 */

const ACCEPT = '.csv,.xlsx'
const MAX_FILE_MB = 5
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const IGNORE = '__ignore__'

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

export function BulkImportDialog({
  open,
  onOpenChange,
  target,
  targets,
  title = 'Bulk Import',
  onComplete,
  extraParams,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fixed target (no picker). */
  target?: string
  /** Override the dynamically-fetched target list (picker mode). */
  targets?: BulkImportTarget[]
  title?: string
  onComplete?: () => void
  /** Context the caller adds to the import, e.g. the group it was started from. */
  extraParams?: Record<string, unknown>
}) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState(target ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [backfill, setBackfill] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  // Manual header -> column overrides on top of the server's auto/saved map.
  const [userMap, setUserMap] = useState<Record<string, string>>({})
  const [rememberMap, setRememberMap] = useState(true)
  // Umbrella: the parent claim's own fields, filled once for the whole import.
  const [parentValues, setParentValues] = useState<Record<string, unknown>>({})

  const effective = target || picked

  // Picker mode (no fixed target): fetch the available targets unless passed.
  const { data: targetsData, isLoading: targetsLoading } = useBulkImportTargets(
    open && !target && !targets,
  )
  const targetOptions = targets ?? targetsData?.targets ?? []

  const { data: schema } = useBulkImportSchema(effective, open && !!effective)
  const validate = useBulkImportValidate(effective)
  const commit = useBulkImportCommit(effective)
  const saveMap = useSaveBulkImportColumnMap(effective)
  const { data: job } = useBulkImportJob(jobId)

  const preview = validate.data
  const done = job?.status === 'completed' || job?.status === 'failed'

  // Tell the caller once per finished job, however the dialog is then closed,
  // so the list behind it refreshes even when the user dismisses with the X.
  const notifiedJob = useRef<string | null>(null)
  useEffect(() => {
    if (done && jobId && notifiedJob.current !== jobId) {
      notifiedJob.current = jobId
      onComplete?.()
    }
  }, [done, jobId, onComplete])

  // Column mapping: the file's headers and where each one lands. The server
  // returns the resolved map (saved + auto-matched) plus the unmapped headers;
  // the user can override any header (or ignore it), and changing one re-checks.
  const headers = useMemo(
    () =>
      preview ? [...Object.keys(preview.mapping), ...preview.unmapped_headers] : [],
    [preview],
  )
  // The target's fields, A to Z, searchable by name or code: a claim type
  // can offer well over a hundred.
  const fieldGroups = useMemo<SearchableSelectGroup[]>(
    () => [
      { label: '', options: [{ value: IGNORE, label: 'Ignore this column' }] },
      {
        label: 'Fields',
        options: (schema?.columns ?? [])
          .map((c) => ({
            value: c.code_name,
            label: c.required ? `${c.display_name} *` : c.display_name,
            keywords: [c.code_name],
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
    ],
    [schema],
  )
  const effectiveTarget = (header: string): string =>
    userMap[header] ?? preview?.mapping[header] ?? ''
  const needsChoice = headers.filter((h) => !effectiveTarget(h))

  // Fill-once form: umbrella claims supply the parent claim's fields once for
  // the whole upload. Required fields gate it.
  const umbrella = schema?.umbrella ?? null
  const hasFillOnce = Boolean(umbrella)
  const fillOnceFields = (umbrella?.parent_columns ?? []).filter(
    (c) => !FILE_FIELD_TYPES.has(c.field_type),
  )
  const isEmpty = (v: unknown) => v === undefined || v === null || v === ''
  const fillOnceMissing = fillOnceFields.filter(
    (f) => f.required && isEmpty(parentValues[f.code_name]),
  )

  // Friendly, recognisable download names from the selected target's label
  // (e.g. "OC HOA Claims" -> "oc-hoa-claims"), falling back to the token.
  const selectedTarget = targetOptions.find((t) => t.token === effective)
  const fileBase =
    (selectedTarget?.label || effective || 'import')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'import'

  const reset = () => {
    setPicked(target ?? '')
    setFile(null)
    setBackfill(false)
    setJobId(null)
    setUserMap({})
    setRememberMap(true)
    setParentValues({})
    validate.reset()
    commit.reset()
  }

  const handlePick = (token: string) => {
    setPicked(token)
    setFile(null)
    setUserMap({})
    setParentValues({})
    validate.reset()
  }

  const close = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const handleFile = (f: File) => {
    if (f.size > MAX_FILE_BYTES) {
      toast.error(`That file is too large (max ${MAX_FILE_MB}MB).`)
      return
    }
    setFile(f)
    setUserMap({})
    validate.mutate(
      { file: f },
      { onError: (err) => toastApiError(err, 'Could not read that file') },
    )
  }

  const remap = (header: string, code: string) => {
    if (!file) return
    const next = { ...userMap, [header]: code }
    setUserMap(next)
    validate.mutate(
      { file, mapping: next },
      { onError: (err) => toastApiError(err, 'Could not re-check the file') },
    )
  }

  const handleImport = () => {
    if (!file) return
    if (rememberMap && Object.keys(userMap).length > 0) {
      // Best-effort: remember the mapping for next time. The import proceeds
      // regardless of whether saving the map succeeds.
      saveMap.mutate({ mappings: userMap })
    }
    commit.mutate(
      {
        file,
        mode: backfill ? 'backfill' : 'live',
        mapping: userMap,
        params:
          hasFillOnce || extraParams
            ? {
                ...extraParams,
                ...(hasFillOnce ? { parent_attribute_values: parentValues } : {}),
              }
            : undefined,
      },
      {
        onSuccess: (res) => setJobId(res.job_id),
        onError: (err) => toastApiError(err, 'Import failed'),
      },
    )
  }

  const downloadTemplate = async () => {
    if (!effective) return
    try {
      const res = await api.get(
        `/bulk-import/${encodeURIComponent(effective)}/template/`,
        { responseType: 'blob' },
      )
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileBase}-template.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toastApiError(err, 'Could not download the template')
    }
  }

  const downloadResults = () => {
    if (!job) return
    const lines = ['Row,Status,Detail']
    for (const r of job.results) {
      const detail =
        r.status === 'created'
          ? Object.values(r.produced).join(' ')
          : r.errors.map((e) => e.message).join('; ')
      lines.push([String(r.row), r.status, csvCell(detail)].join(','))
    }
    const blob = new Blob([lines.join('\n') + '\n'], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${fileBase}-import-results.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const previewColumns: DataTableColumn<BulkImportPreviewRow>[] = useMemo(
    () => [
      { id: 'row', header: 'Row', accessorKey: 'row' },
      {
        id: 'status',
        header: 'Status',
        cell: (r) =>
          r.status === 'ok' ? (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 size={12} className="text-[var(--green)]" />
              Ready
            </Badge>
          ) : (
            <Badge variant="destructive" className="gap-1">
              <XCircle size={12} />
              Error
            </Badge>
          ),
      },
      {
        id: 'detail',
        header: 'Detail',
        cell: (r) =>
          r.status === 'ok' ? (
            <span className="text-[12px] text-[var(--ink-2)]">
              {Object.values(r.resolved).join(' ') || 'Ready'}
            </span>
          ) : (
            <span className="text-[12px] text-[var(--red)]">
              {r.errors.map((e) => e.message).join('; ')}
            </span>
          ),
      },
    ],
    [],
  )

  const resultColumns: DataTableColumn<BulkImportJobResultRow>[] = useMemo(
    () => [
      { id: 'row', header: 'Row', accessorKey: 'row' },
      {
        id: 'status',
        header: 'Status',
        cell: (r) =>
          r.status === 'created' ? (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 size={12} className="text-[var(--green)]" />
              Imported
            </Badge>
          ) : (
            <Badge variant="destructive" className="gap-1">
              <XCircle size={12} />
              {r.status === 'skipped' ? 'Skipped' : 'Failed'}
            </Badge>
          ),
      },
      {
        id: 'detail',
        header: 'Detail',
        cell: (r) => (
          <span className="text-[12px] text-[var(--ink-2)]">
            {r.errors.map((e) => e.message).join('; ') ||
              Object.values(r.produced).join(' ')}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="w-[94vw] max-w-[560px] sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Upload a CSV or Excel file. We will check it, then import the rows.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex max-h-[64vh] flex-col gap-4 overflow-y-auto">
          {/* Phase: results */}
          {done && job ? (
            <div className="flex flex-col gap-3">
              {job.error && (
                <p className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--red)]/30 bg-[var(--red)]/10 px-3 py-2.5 text-[12px] text-[var(--red)]">
                  <XCircle size={14} className="mt-0.5 shrink-0" />
                  {job.error}
                </p>
              )}
              <div className="flex items-center gap-3 text-[13px] text-[var(--ink-2)]">
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle2 size={12} className="text-[var(--green)]" />
                  {job.created} imported
                </Badge>
                {job.failed > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle size={12} />
                    {job.failed} failed
                  </Badge>
                )}
                {job.skipped > 0 && (
                  <Badge variant="outline">{job.skipped} skipped</Badge>
                )}
              </div>
              {job.results.length > 0 && (
                <div className="max-h-[360px] overflow-y-auto">
                  <DataTable
                    tableId="bulk-import-results"
                    columns={resultColumns}
                    data={job.results}
                    rowKey={(r) => String(r.row)}
                  />
                </div>
              )}
            </div>
          ) : jobId ? (
            /* Phase: processing */
            <div className="flex flex-col items-center gap-2 py-10 text-[13px] text-[var(--ink-2)]">
              <Loader2 className="h-5 w-5 animate-spin text-[var(--green)]" />
              Importing {job?.total ?? ''} record(s). You can keep working - we
              will finish in the background.
            </div>
          ) : (
            /* Phase: pick + preview */
            <>
              {!target && (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    What are you importing?
                  </Label>
                  {/* Controlled from the first render: `|| undefined` made the
                      Select uncontrolled until a type was picked, which is the
                      uncontrolled-to-controlled switch Base UI warns about.
                      SelectValue already renders the placeholder for "". */}
                  <Select
                    value={picked}
                    onValueChange={handlePick}
                    disabled={!!jobId}
                  >
                    <SelectTrigger className="h-9 text-[12px]">
                      <SelectValue placeholder="Choose a claim type" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetOptions.map((t) => (
                        <SelectItem key={t.token} value={t.token}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!targetsLoading && targetOptions.length === 0 && (
                    <p className="text-[11px] text-[var(--ink-3)]">
                      {t(
                        'bulkImport.noTypesEnabled',
                        'No claim types are enabled for bulk upload yet. Contact iWarranty support to switch one on for your organisation.',
                      )}
                    </p>
                  )}
                </div>
              )}

              {effective && (
                <>
                  <div className="flex items-center justify-between rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2.5">
                    <span className="text-[12px] text-[var(--ink-2)]">
                      Need a starting point? Download the template.
                    </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  disabled={!schema}
                  className="cursor-pointer gap-1.5"
                >
                  <Download size={13} />
                  Template
                </Button>
              </div>

              {!file && (
                <FileDropzone
                  accept={ACCEPT}
                  onFile={handleFile}
                  hint="CSV or Excel (.csv, .xlsx)"
                />
              )}

              {file && (
                <div className="flex items-center justify-between text-[12px] text-[var(--ink-2)]">
                  <span className="flex items-center gap-2">
                    <Upload size={13} className="text-[var(--green)]" />
                    {file.name}
                    {validate.isPending && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={reset}
                    className="h-auto cursor-pointer p-0 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)] hover:text-[var(--ink)]"
                  >
                    Choose another file
                  </Button>
                </div>
              )}

              {preview && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3 text-[12px] text-[var(--ink-2)]">
                    <span>
                      {preview.ok} of {preview.total} row(s) ready
                      {preview.failed > 0 ? `, ${preview.failed} with errors` : ''}.
                    </span>
                  </div>
                  {preview.total > 0 && preview.ok === 0 && (
                    <p className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--red)]/30 bg-[var(--red)]/10 px-3 py-2.5 text-[12px] text-[var(--red)]">
                      <XCircle size={14} className="mt-0.5 shrink-0" />
                      {t('bulkImport.noRowsReady', 'No rows are ready to import. Fix the errors shown below (or remap the columns above), then the import will run.')}
                    </p>
                  )}
                  {headers.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                          Column mapping
                        </Label>
                        {needsChoice.length > 0 && (
                          <span className="text-[11px] text-[var(--ink-3)]">
                            {needsChoice.length} column(s) unmapped
                          </span>
                        )}
                      </div>
                      <div className="flex max-h-[180px] flex-col divide-y divide-[var(--rule)] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
                        {headers.map((h) => {
                          const value = effectiveTarget(h)
                          return (
                            <div
                              key={h}
                              className="flex items-center gap-3 px-3 py-2"
                            >
                              <span
                                className="min-w-0 flex-1 truncate text-[12px] text-[var(--ink)]"
                                title={h}
                              >
                                {h}
                              </span>
                              <SearchableSelect
                                value={value}
                                onValueChange={(v) => remap(h, v)}
                                groups={fieldGroups}
                                disabled={validate.isPending}
                                placeholder="Choose field"
                                searchPlaceholder="Search fields"
                                emptyMessage="No field matches"
                                className="w-[240px] shrink-0"
                                triggerClassName={`h-8 text-[12px] ${!value ? 'border-[var(--green)]' : ''}`}
                              />
                            </div>
                          )
                        })}
                      </div>
                      <Label className="flex cursor-pointer items-center gap-2 text-[12px] text-[var(--ink-2)]">
                        <Checkbox
                          checked={rememberMap}
                          onCheckedChange={(v) => setRememberMap(v === true)}
                        />
                        Remember this mapping next time
                      </Label>
                    </div>
                  )}
                  {hasFillOnce && fillOnceFields.length > 0 && (
                    <div className="flex flex-col gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-3">
                      <Label className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                        {selectedTarget?.label
                          ? `${selectedTarget.label} details`
                          : 'Details'}{' '}
                        (filled once)
                      </Label>
                      {fillOnceFields.map((f) => (
                        <MetadataField
                          key={f.code_name}
                          field={normaliseClaimField({
                            code_name: f.code_name,
                            display_name: f.display_name,
                            field_type: f.field_type,
                            required: Boolean(f.required),
                            description: f.description,
                            configuration_data: f.configuration_data,
                          })}
                          value={parentValues[f.code_name]}
                          onChange={(v) =>
                            setParentValues((prev) => ({ ...prev, [f.code_name]: v }))
                          }
                          labelVariant="compact"
                        />
                      ))}
                    </div>
                  )}
                  <div className="max-h-[300px] overflow-y-auto">
                    <DataTable
                      tableId="bulk-import-preview"
                      columns={previewColumns}
                      data={preview.rows}
                      rowKey={(r) => String(r.row)}
                    />
                  </div>
                  <Label className="flex cursor-pointer items-start gap-2 text-[12px] text-[var(--ink-2)]">
                    <Checkbox
                      checked={backfill}
                      onCheckedChange={(v) => setBackfill(v === true)}
                      className="mt-0.5"
                    />
                    <span className="flex flex-col">
                      Backfill (do not notify)
                      <span className="text-[11px] text-[var(--ink-3)]">
                        On for importing existing records silently. Off sends the
                        normal emails and notifications.
                      </span>
                    </span>
                  </Label>
                </div>
              )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {done ? (
            <>
              <Button
                variant="outline"
                onClick={downloadResults}
                className="cursor-pointer gap-1.5"
              >
                <Download size={13} />
                Download results
              </Button>
              <Button
                onClick={() => close(false)}
                className="cursor-pointer"
              >
                Done
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => close(false)}
                className="cursor-pointer"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={
                  !preview ||
                  preview.ok === 0 ||
                  fillOnceMissing.length > 0 ||
                  commit.isPending ||
                  !!jobId
                }
                className="cursor-pointer gap-1.5"
              >
                {(commit.isPending || (!!jobId && !done)) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Import {preview ? `${preview.ok} ` : ''}Record(s)
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
